import { SectionTitle } from "@/components/Title";
import { SizingWrapper } from "@/components/Wrapper";
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  Grid,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { LoadingButton } from "@mui/lab";
import React from "react";
import type {
  SearchSectionProps,
  SearchCondition,
  IReasonGroupOption,
} from "../types";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, useWatch, type Control } from "react-hook-form";
import { Label } from "@/components/Label";
import { DateRangePicker } from "@/components/DatePicker";
import {
  OTHER_REASON_OPTION_LABEL,
  OTHER_REASON_OPTION_VALUE,
} from "../types/enums";

export const searchConditionSchema = z
  .object({
    prefectures: z.array(z.number()),
    location: z.string(),
    debtorName: z.string(),
    mortgageeName: z.string(),
    reasonOption: z.string(),
    reasonFreeText: z.string(),
    acceptanceDateRange: z
      .tuple([z.date().nullable(), z.date().nullable()])
      .superRefine(([startDate, endDate], ctx) => {
        if (startDate !== null && endDate !== null && startDate > endDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [0],
            message: "開始日は終了日以前を入力してください",
          });
        }
      }),
    hasJointSecurity: z.boolean().default(false),
    isCurrentOnly: z.boolean().default(false),
  })
  .refine(
    (args) => {
      const { debtorName, mortgageeName } = args;
      return debtorName.trim().length > 0 || mortgageeName.trim().length > 0;
    },
    {
      message: "債務者名または抵当権者名のいずれかを入力してください。",
      path: ["debtorName"],
    }
  );

export type SearchConditionFormSchema = z.infer<typeof searchConditionSchema>;

/**
 * 登記原因プルダウンに並べる選択肢を組み立てる
 *
 * マスタ由来の選択肢の先頭に「指定なし」、末尾にFE専用の「その他」を付加する。
 */
export const buildReasonMenuOptions = (
  reasonOptions: IReasonGroupOption[]
): IReasonGroupOption[] => {
  const unspecifiedOption: IReasonGroupOption = {
    value: "",
    label: "指定なし",
    category: "",
  };
  const otherOption: IReasonGroupOption = {
    value: OTHER_REASON_OPTION_VALUE,
    label: OTHER_REASON_OPTION_LABEL,
    category: "",
  };

  return [unspecifiedOption, ...reasonOptions, otherOption];
};

/**
 * フォームの入力値から検索APIに渡す登記原因（自由入力）を組み立てる
 *
 * 「その他」選択時のみ自由入力の値を渡す。それ以外は区分IDで検索するためnullとする。
 */
export const buildReason = (data: SearchConditionFormSchema): string | null => {
  if (data.reasonOption !== OTHER_REASON_OPTION_VALUE) return null;

  const freeText = data.reasonFreeText.trim();
  return freeText !== "" ? freeText : null;
};

/**
 * フォームの入力値から検索APIに渡す登記原因区分のIDを組み立てる
 *
 * 未選択および「その他」選択時は区分での絞り込みを行わないためnullとする。
 */
export const buildReasonGroupId = (
  data: SearchConditionFormSchema
): number | null => {
  if (
    data.reasonOption === "" ||
    data.reasonOption === OTHER_REASON_OPTION_VALUE
  ) {
    return null;
  }

  return Number(data.reasonOption);
};

/**
 * 「その他（自由入力）」選択時だけ表示する登記原因の自由入力欄
 *
 * 選択値の購読をこの末端コンポーネントに閉じるためにuseWatchを使う。
 * 親（SearchSection）でwatchすると登記原因を選ぶたびにフォーム全体が
 * 再描画されるため、表示切り替えに必要なこの範囲だけを購読させる。
 */
const ReasonFreeTextField: React.FC<{
  control: Control<SearchConditionFormSchema>;
}> = ({ control }) => {
  const reasonOption = useWatch({ control, name: "reasonOption" });

  if (reasonOption !== OTHER_REASON_OPTION_VALUE) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Controller
        name="reasonFreeText"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            fullWidth
            id="reasonFreeText"
            label="登記原因（自由入力）"
            type="text"
            error={!!fieldState.error}
            helperText={fieldState.error ? fieldState.error.message : ""}
          />
        )}
      />
    </Box>
  );
};

export const SearchSection: React.FC<SearchSectionProps> = ({
  handleSearch,
  prefectures,
  reasonOptions,
  isLoading,
}) => {
  const {
    control,
    getValues,
    handleSubmit,
    formState: { errors },
    reset,
    trigger,
    setError,
    setValue,
  } = useForm<SearchConditionFormSchema>({
    mode: "all",
    defaultValues: {
      prefectures: [],
      location: "",
      debtorName: "",
      mortgageeName: "",
      reasonOption: "",
      reasonFreeText: "",
      acceptanceDateRange: [null, null],
      hasJointSecurity: false,
      isCurrentOnly: false,
    },
    resolver: zodResolver(searchConditionSchema),
  });

  const handleDatePickerErrorChange = (index: 0 | 1) => {
    return (error: unknown): void => {
      if (error === null || error === "minDate" || error === "maxDate") {
        void trigger("acceptanceDateRange");
        return;
      }
      const errorPath = (
        ["acceptanceDateRange.0", "acceptanceDateRange.1"] as const
      )[index];
      setError(errorPath, {
        type: "manual",
        message: "受付年月日の形式が正しくありません",
      });
    };
  };

  const onSubmit = (data: SearchConditionFormSchema): void => {
    const condition: SearchCondition = {
      prefectures: data.prefectures,
      location: data.location.trim() !== "" ? data.location.trim() : null,
      debtorName: data.debtorName.trim() !== "" ? data.debtorName.trim() : null,
      mortgageeName:
        data.mortgageeName.trim() !== "" ? data.mortgageeName.trim() : null,
      reason: buildReason(data),
      reasonGroupId: buildReasonGroupId(data),
      acceptanceDateFrom: data.acceptanceDateRange[0],
      acceptanceDateTo: data.acceptanceDateRange[1],
      hasJointSecurity: data.hasJointSecurity ? true : null,
      isCurrentOnly: data.isCurrentOnly ? true : null,
    };
    handleSearch(condition);
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <SectionTitle>検索</SectionTitle>
      </Box>

      <Box sx={{ mx: 2, mb: 2 }}>
        <Divider />
      </Box>

      <form
        onSubmit={
          handleSubmit(onSubmit) as React.FormEventHandler<HTMLFormElement>
        }
      >
        {/* 都道府県 */}
        <Box sx={{ mx: 2, mb: 2 }}>
          <SizingWrapper>
            <Label>都道府県</Label>
            <Controller
              name="prefectures"
              control={control}
              render={({ field, formState: { errors } }) => (
                <div>
                  <FormControl fullWidth>
                    <InputLabel
                      id="prefecture-multiple-checkbox-label"
                      error={!!errors.prefectures}
                    >
                      都道府県
                    </InputLabel>
                    <Select
                      {...field}
                      id="prefecture-multiple-checkbox"
                      error={!!errors.prefectures}
                      input={
                        <OutlinedInput
                          label="都道府県"
                          id="prefecture-multiple-chip"
                        />
                      }
                      labelId="prefecture-multiple-checkbox-label"
                      multiple
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => {
                            const selectPrefecture = prefectures.find(
                              (prefecture) => prefecture.id === value
                            );

                            return (
                              <Chip
                                key={value}
                                label={selectPrefecture?.name}
                                sx={{ cursor: "pointer" }}
                              />
                            );
                          })}
                        </Box>
                      )}
                      endAdornment={
                        field.value.length > 0 ? (
                          <IconButton
                            sx={{
                              marginRight: "10px",
                            }}
                            onClick={() => {
                              reset({
                                ...getValues(),
                                prefectures: [],
                              });
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        ) : null
                      }
                    >
                      {prefectures.map((prefecture) => (
                        <MenuItem key={prefecture.id} value={prefecture.id}>
                          <Checkbox
                            checked={field.value.includes(prefecture.id)}
                          />
                          <ListItemText primary={prefecture.name} />
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText error>
                      {errors?.prefectures?.message}
                    </FormHelperText>
                  </FormControl>
                </div>
              )}
            />
          </SizingWrapper>
        </Box>

        {/* 所在 */}
        <Box sx={{ mx: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Label>所在</Label>
              <Controller
                name="location"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    id="location"
                    label="所在"
                    type="text"
                    error={!!errors.location}
                    helperText={errors.location ? errors.location.message : ""}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        {/* 債務者・抵当権者情報 */}
        <Box sx={{ mx: 2, mb: 2 }}>
          <Label>
            債務者・抵当権者情報
            <Typography
              color="error"
              component="span"
              sx={{ ml: 1, fontSize: "0.85rem", fontWeight: "bold" }}
            >
              *いずれか必須
            </Typography>
          </Label>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                債務者名 (部分一致)
              </Typography>
              <Controller
                name="debtorName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    id="debtorName"
                    placeholder="債務者名"
                    type="text"
                    error={!!errors.debtorName}
                    helperText={
                      errors.debtorName ? errors.debtorName.message : ""
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                抵当権者名(部分一致)
              </Typography>
              <Controller
                name="mortgageeName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    id="mortgageeName"
                    placeholder="抵当権者名"
                    type="text"
                    error={!!errors.mortgageeName}
                    helperText={
                      errors.mortgageeName ? errors.mortgageeName.message : ""
                    }
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        {/* 登記原因 */}
        <Box sx={{ mx: 2, mb: 2 }}>
          <Label>登記原因</Label>
          <Controller
            name="reasonOption"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel id="reason-option-label">登記原因</InputLabel>
                <Select
                  {...field}
                  id="reasonOption"
                  labelId="reason-option-label"
                  label="登記原因"
                  MenuProps={{
                    PaperProps: { sx: { maxWidth: "none" } },
                    sx: { "& .MuiMenuItem-root": { whiteSpace: "normal" } },
                  }}
                  onChange={(event) => {
                    field.onChange(event);
                    // 「その他」以外に切り替えたときは、隠れた自由入力が送信されないようクリアする
                    if (event.target.value !== OTHER_REASON_OPTION_VALUE) {
                      setValue("reasonFreeText", "");
                    }
                  }}
                >
                  {buildReasonMenuOptions(reasonOptions).map(
                    (option, index, options) => {
                      const isCategoryHead =
                        option.category !== "" &&
                        option.category !== options[index - 1]?.category;

                      // 自由入力の選択肢はマスタ由来の区分と区別できるよう区切り線で分ける
                      const isFreeTextOption =
                        option.value === OTHER_REASON_OPTION_VALUE;

                      return [
                        isCategoryHead ? (
                          <ListSubheader key={`category-${option.category}`}>
                            {option.category}
                          </ListSubheader>
                        ) : null,
                        isFreeTextOption ? (
                          <Divider key="free-text-divider" />
                        ) : null,
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>,
                      ];
                    }
                  )}
                </Select>
              </FormControl>
            )}
          />

          {/* 「その他」選択時のみ、従来同様の自由入力での条件指定を可能にする */}
          <ReasonFreeTextField control={control} />
        </Box>

        {/* 受付年月日 */}
        <Box sx={{ mx: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Controller
                    name="acceptanceDateRange"
                    control={control}
                    render={({ field: { value, onChange }, fieldState }) => {
                      const rangeErrors = fieldState.error as
                        | Array<{ message?: string } | undefined>
                        | undefined;
                      return (
                        <DateRangePicker
                          startDateLabel="受付年月日 開始"
                          startDate={value[0]}
                          onChangeStartDate={(date) => {
                            onChange([date, value[1]]);
                          }}
                          endDateLabel="受付年月日 終了"
                          endDate={value[1]}
                          onChangeEndDate={(date) => {
                            onChange([value[0], date]);
                          }}
                          displayDateLabel={true}
                          onStartDateErrorChange={handleDatePickerErrorChange(
                            0
                          )}
                          onEndDateErrorChange={handleDatePickerErrorChange(1)}
                          errorStart={rangeErrors?.[0]?.message}
                          errorEnd={rangeErrors?.[1]?.message}
                        />
                      );
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* 共同担保 */}
        <Box sx={{ mx: 2, mb: 2 }}>
          <Label>共同担保</Label>
          <FormControlLabel
            control={
              <Controller
                name="hasJointSecurity"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    name="hasJointSecurity"
                    checked={field.value}
                    onChange={(event) => {
                      field.onChange(event.target.checked);
                    }}
                  />
                )}
              />
            }
            label="共同担保目録に記載がある登記のみを検索する"
          />
        </Box>

        {/* 現在事項 */}
        <Box sx={{ mx: 2, mb: 2 }}>
          <Label>現在事項</Label>
          <FormControlLabel
            control={
              <Controller
                name="isCurrentOnly"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    name="isCurrentOnly"
                    checked={field.value}
                    onChange={(event) => {
                      field.onChange(event.target.checked);
                    }}
                  />
                )}
              />
            }
            label="現在事項のみを検索する"
          />
        </Box>

        {/* ボタン */}
        <Box
          sx={{
            mr: 2,
            mb: 3,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isLoading}
            loadingIndicator="検索中..."
          >
            この条件で検索
          </LoadingButton>
        </Box>
      </form>
    </>
  );
};
